import { Injectable, NgZone, computed, inject, signal } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { fromEvent, interval, Subject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { DeviceDetectorService } from './device-detector.service';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

type RuntimeEnv = 'twa' | 'pwa' | 'browser';

type ApkVersionConfig = {
  /** Minimum supported APK versionCode. If current < minApk => force update */
  minApk: number;
  /** Latest available APK versionCode (optional, can be used for soft updates) */
  latestApk?: number;
  /** Where to send the user to update (Play Store or direct APK link) */
  storeUrl: string;
};

@Injectable({ providedIn: 'root' })
export class PwaManagerService {
  private destroy$ = new Subject<void>();
  private deviceDetectorService = inject(DeviceDetectorService);

  // -----------------------------------------------------------------------
  // Public Reactive State (Signals)
  // -----------------------------------------------------------------------

  /** Indicates whether the install prompt is currently available */
  readonly canInstall = signal(false);

  /** Indicates whether a new service worker version is available */
  readonly updateAvailable = signal(false);

  /** Indicates whether the app has been installed as a PWA */
  readonly isInstalled = signal(false);

  /** Persists whether the user has dismissed the install banner */
  readonly installDismissed = signal(
    localStorage.getItem('pwa_install_dismissed') === '1'
  );

  /** Indicates whether the APK update is required (TWA only) */
  readonly apkUpdateRequired = signal(false);

  /** APK update link (Play Store or direct APK link) */
  readonly apkStoreUrl = signal<string | null>(null);

  /** Optional translation key/message key for APK update banner */
  readonly apkUpdateMessageKey = signal<string>('APPLICATION_APK.UPDATE_REQUIRED');

  // -----------------------------------------------------------------------
  // Private State
  // -----------------------------------------------------------------------

  private installEvent: BeforeInstallPromptEvent | null = null;
  private readonly reloadGuardKey = 'pwa_reloaded_once';

  /** Endpoint that returns APK version requirements */
  private readonly apkVersionConfigUrl = '/version.json';

  /** Persists initial runtime source across SPA navigations */
  private readonly runtimeSourceKey = 'tc_runtime_source';

  constructor(private swUpdate: SwUpdate, private zone: NgZone) { }

  /**
   * Initializes PWA logic.
   * Should be called once inside AppComponent.ngOnInit().
   */
  init(): void {
    // Disable UI logic on non-mobile devices if needed
    if (!this.deviceDetectorService.isMobileOrTabletAcivePwa()) {
      this.resetUiState();
      return;
    }

    this.listenInstallPrompt();
    this.listenInstalled();
    this.listenSwUpdates();

    // Improve update detection responsiveness
    this.checkForUpdateOnce();
    this.checkForUpdateOnVisibility();
    this.periodicUpdateCheck();

    // Check APK version requirements (TWA only)
    this.checkApkUpdate();
  }

  /**
   * Optional cleanup if manual lifecycle handling is required.
   */
  destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // -----------------------------------------------------------------------
  // Runtime Environment Detection
  // -----------------------------------------------------------------------
  /**
   * Detects execution inside Android TWA (APK container).
   * The Android launchUrl must append query parameters:
   *   ?source=twa&apk=<versionCode>
   * Example:
   *   https://example.com/?source=twa&apk=123
   *
   * This allows:
   * - Differentiating TWA from PWA (Add to Home Screen)
   * - Enforcing minimum APK version if needed
   */

  /** Returns a query parameter value from the current URL */
  private getQueryParam(key: string): string | null {
    const params = new URLSearchParams(window.location.search);
    const value = params.get(key);

    // Persist runtime source across SPA navigations where query params may disappear
    if (key === 'source') {
      if (value) sessionStorage.setItem(this.runtimeSourceKey, value);
      return value ?? sessionStorage.getItem(this.runtimeSourceKey);
    }

    return value;
  }

  /** True when running inside a Trusted Web Activity (requires ?source=twa) */
  readonly isTwa = computed(() => this.getQueryParam('source') === 'twa');

  /** True when running as an installed PWA (requires ?source=pwa in start_url) */
  readonly isPwa = computed(() => this.getQueryParam('source') === 'pwa');

  /** APK version passed via query parameter (?apk=123) */
  readonly apkVersion = computed(() => {
    const v = this.getQueryParam('apk');
    if (!v) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  });

  /** True when running as an installed PWA (Add to Home Screen) */
  readonly isStandalonePwa = computed(() => {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: fullscreen)').matches ||
      (navigator as any).standalone === true
    );
  });

  /** Unified runtime type */
  readonly runtime = computed<RuntimeEnv>(() => {
    if (this.isTwa()) return 'twa';
    if (this.isPwa()) return 'pwa';
    return 'browser';
  });

  /** True when running in a regular browser tab */
  readonly isBrowser = computed(() => this.runtime() === 'browser');

  // -----------------------------------------------------------------------
  // UI State Computed Signals
  // -----------------------------------------------------------------------

  /**
   * Controls visibility of the install banner.
   * Disabled inside TWA to prevent duplicate app icons.
   */
  readonly shouldShowInstall = computed(() => {
    if (this.runtime() === 'twa') return false;

    return this.canInstall() && !this.installDismissed() && !this.isInstalled();
  });

  /**
   * Controls visibility of the update banner.
   * Shown only for TWA or installed PWA environments.
   */
  readonly shouldShowUpdate = computed(() => {
    const runtime = this.runtime();
    const validRuntime = runtime === 'twa' || runtime === 'pwa';

    return (
      this.deviceDetectorService.isMobileOrTabletAcivePwa() &&
      validRuntime &&
      this.updateAvailable()
    );
  });

  /** High-level UI status helper */
  readonly status = computed(() => {
    if (this.apkUpdateRequired()) return 'apk-update-required';
    if (this.shouldShowUpdate()) return 'update-available';
    if (this.shouldShowInstall()) return 'show-install';
    if (this.isInstalled()) return 'installed';
    return 'idle';
  });

  // -----------------------------------------------------------------------
  // Install Banner Controls
  // -----------------------------------------------------------------------

  /** Dismisses the install banner and persists the preference */
  dismissInstallBanner(): void {
    this.installDismissed.set(true);
    localStorage.setItem('pwa_install_dismissed', '1');
  }

  /** Resets the install dismissal state */
  resetInstallBannerDismiss(): void {
    this.installDismissed.set(false);
    localStorage.removeItem('pwa_install_dismissed');
  }

  // -----------------------------------------------------------------------
  // Install Logic
  // -----------------------------------------------------------------------

  /**
   * Triggers the PWA install prompt.
   * Returns the user's choice outcome.
   */
  async install(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    if (this.runtime() === 'twa') return 'unavailable';
    if (!this.deviceDetectorService.isMobileOrTabletAcivePwa()) return 'unavailable';
    if (!this.installEvent) return 'unavailable';

    await this.installEvent.prompt();
    const choice = await this.installEvent.userChoice;

    this.installEvent = null;
    this.canInstall.set(false);

    if (choice.outcome === 'dismissed') {
      this.dismissInstallBanner();
    }

    return choice.outcome;
  }

  // -----------------------------------------------------------------------
  // Service Worker Update Logic
  // -----------------------------------------------------------------------

  /**
   * Activates the new service worker and reloads the application.
   * Includes a guard to prevent infinite reload loops.
   */
  async applyUpdateAndReload(): Promise<void> {
    if (sessionStorage.getItem(this.reloadGuardKey)) return;
    sessionStorage.setItem(this.reloadGuardKey, '1');

    if (this.swUpdate.isEnabled) {
      try {
        await this.swUpdate.activateUpdate();
      } catch {
        // Ignore activation errors and fallback to reload
      }
    }

    document.location.reload();
  }

  /** Hides the update notification */
  dismissUpdate(): void {
    this.updateAvailable.set(false);
  }

  // -----------------------------------------------------------------------
  // APK Update Logic (TWA Only)
  // -----------------------------------------------------------------------

  /**
   * Fetches APK version requirements from server and determines whether an APK update is required.
   * Expected response example:
   * { "minApk": 120, "latestApk": 123, "storeUrl": "https://play.google.com/store/apps/details?id=..." }
   */
  async checkApkUpdate(): Promise<void> {
    if (this.runtime() !== 'twa') {
      this.apkUpdateRequired.set(false);
      this.apkStoreUrl.set(null);
      return;
    }

    const currentApk = this.apkVersion();
    if (!currentApk) {
      this.apkUpdateRequired.set(false);
      this.apkStoreUrl.set(null);
      return;
    }

    try {
      const res = await fetch(this.apkVersionConfigUrl, { cache: 'no-store' });
      if (!res.ok) return;

      const cfg = (await res.json()) as ApkVersionConfig;

      const minApk = Number(cfg.minApk);
      const storeUrl = String(cfg.storeUrl || '');

      if (!Number.isFinite(minApk) || !storeUrl) return;

      // Force update if current APK is below the minimum supported version
      if (currentApk < minApk) {
        this.apkUpdateRequired.set(true);
        this.apkStoreUrl.set(storeUrl);
      } else {
        this.apkUpdateRequired.set(false);
        this.apkStoreUrl.set(null);
      }
    } catch {
      // Ignore network errors
    }
  }

  /** Opens the APK update link (Play Store or direct APK link) */
  openApkUpdateLink(): void {
    const url = this.apkStoreUrl();
    if (!url) return;
    window.location.href = url;
  }

  // -----------------------------------------------------------------------
  // Service Worker Disable + Cache Cleanup
  // -----------------------------------------------------------------------

  /**
   * Fully disables PWA behavior at runtime:
   * - Unregisters service workers
   * - Clears Angular ngsw caches
   */
  async disablePwaAndClearNgSwCaches(): Promise<void> {
    if (!this.deviceDetectorService.isMobileOrTabletAcivePwa()) return;

    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }

    if ('caches' in window) {
      const keys = await caches.keys();
      const ngswKeys = keys.filter(
        (k) => k.startsWith('ngsw:') || k.startsWith('ngsw-cache')
      );
      await Promise.all(ngswKeys.map((k) => caches.delete(k)));
    }

    this.installEvent = null;
    this.resetUiState();
  }

  // -----------------------------------------------------------------------
  // Internal Event Listeners
  // -----------------------------------------------------------------------

  private listenInstallPrompt(): void {
    fromEvent<BeforeInstallPromptEvent>(window, 'beforeinstallprompt')
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        if (!this.deviceDetectorService.isMobileOrTabletAcivePwa()) return;
        if (this.runtime() === 'twa') return;

        event.preventDefault();

        if (this.installDismissed()) return;

        this.installEvent = event;
        this.canInstall.set(true);
      });
  }

  private listenInstalled(): void {
    fromEvent(window, 'appinstalled')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.deviceDetectorService.isMobileOrTabletAcivePwa()) return;

        this.isInstalled.set(true);
        this.installEvent = null;
        this.canInstall.set(false);

        this.installDismissed.set(true);
        localStorage.setItem('pwa_install_dismissed', '1');
      });
  }

  private listenSwUpdates(): void {
    if (!this.swUpdate.isEnabled) return;

    this.swUpdate.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (!this.deviceDetectorService.isMobileOrTabletAcivePwa()) return;

        this.updateAvailable.set(true);
      });
  }

  // -----------------------------------------------------------------------
  // Update Checks
  // -----------------------------------------------------------------------

  private checkForUpdateOnce(): void {
    if (!this.swUpdate.isEnabled) return;
    this.swUpdate.checkForUpdate().catch(() => { });
  }

  private checkForUpdateOnVisibility(): void {
    if (!this.swUpdate.isEnabled) return;

    fromEvent(document, 'visibilitychange')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (document.visibilityState === 'visible') {
          this.swUpdate.checkForUpdate().catch(() => { });
          // Re-check APK requirements when user returns to the app
          this.checkApkUpdate();
        }
      });
  }

  private periodicUpdateCheck(): void {
    if (!this.swUpdate.isEnabled) return;

    this.zone.runOutsideAngular(() => {
      interval(5 * 60 * 1000)
        .pipe(
          takeUntil(this.destroy$),
          switchMap(() => this.swUpdate.checkForUpdate())
        )
        .subscribe({
          error: () => {
            // Prevent interval stream from breaking on error
          }
        });
    });
  }

  private resetUiState(): void {
    this.canInstall.set(false);
    this.updateAvailable.set(false);

    this.apkUpdateRequired.set(false);
    this.apkStoreUrl.set(null);
  }
}